/*
 * Brick 6 (N16) — Sidebar referral card.
 * Small dopamine-hit widget dealers see on every navigation.
 * Copies their unique referral link to clipboard, shows a live "months banked" counter,
 * and deep-links to the full referrals page for history/audit.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, ExternalLink } from "lucide-react";

import { useTranslation } from "react-i18next";
export default function SidebarReferralCard({ collapsed = false }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    let dead = false;
    api.get("/dealer/referrals")
      .then((r) => !dead && setData(r.data))
      .catch(() => {}); // silently skip — not every user is a dealer
    return () => { dead = true; };
  }, []);

  if (!data?.code) return null;

  const link = data.link || `${window.location.origin}/register?ref=${data.code}`;

  const copy = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t("components.sidebarreferralcard.referral_link_copied_share_on_whatsapp"));
    } catch {
      toast.error("Couldn't copy — link: " + link);
    }
  };

  if (collapsed) {
    // Sidebar-collapsed variant: just a small gift icon that copies on click
    return (
      <button
        onClick={copy}
        className="mx-auto mt-2 flex h-9 w-9 items-center justify-center rounded-full border border-emerald-300/60 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
        title={`Copy your referral link · ${data.months_banked} months banked so far`}
        data-testid="sidebar-referral-icon"
      >
        <Gift className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div
      className="mt-3 rounded-xl border border-emerald-300/60 bg-gradient-to-br from-emerald-500/[0.08] via-card to-teal-500/[0.05] p-3 dark:border-emerald-900/60"
      data-testid="sidebar-referral-card"
    >
      <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-emerald-800 dark:text-emerald-200">
        <Gift className="h-3 w-3" /> Refer a pump
      </div>
      <div className="mt-1 text-[11px] leading-snug text-foreground">
        Both dealers get <b>2 free months</b> when the referred pump is verified by HisabX.
      </div>

      <div className="mt-2 flex items-stretch gap-1">
        <div
          className="min-w-0 flex-1 truncate rounded-md border border-border bg-background px-2 py-1 font-mono text-[10px] text-muted-foreground"
          title={link}
        >
          {data.code}
        </div>
        <button
          onClick={copy}
          className="flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[10px] font-bold text-white hover:bg-emerald-700"
          data-testid="sidebar-referral-copy"
        >
          <Copy className="h-3 w-3" /> Copy
        </button>
      </div>

      {data.counts?.total > 0 && (
        <div className="mt-2 grid grid-cols-3 gap-1 text-center text-[10px]">
          <MiniCounter label={t("csa.csaleaves.pending")} value={data.counts.pending} tone="amber" />
          <MiniCounter label={t("csa.csaleaves.approved")} value={data.counts.approved} tone="emerald" />
          <MiniCounter label={t("components.sidebarreferralcard.banked")} value={`${data.months_banked}mo`} tone="sky" />
        </div>
      )}

      <Link
        to="/dealer/referrals"
        className="mt-2 flex items-center justify-between text-[10px] font-semibold text-emerald-700 hover:underline dark:text-emerald-300"
        data-testid="sidebar-referral-more"
      >
        View history
        <ExternalLink className="h-2.5 w-2.5" />
      </Link>
    </div>
  );
}

function MiniCounter({ label, value, tone }) {
  const tones = {
    amber: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
    emerald: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300",
    sky: "bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300",
  };
  return (
    <div className={"rounded px-1 py-0.5 " + tones[tone]}>
      <div className="font-black tabular-nums">{value}</div>
      <div className="text-[9px] uppercase tracking-widest">{label}</div>
    </div>
  );
}
